import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Post, PostFilters, GeneratePostRequest, CalendarEntry, ContentStats } from './types'
import { useApi } from '@/hooks/use-api'

// Query keys
export const contentKeys = {
  all: ['content'] as const,
  posts: () => [...contentKeys.all, 'posts'] as const,
  postList: (filters: PostFilters) => [...contentKeys.posts(), filters] as const,
  post: (id: string) => [...contentKeys.all, 'post', id] as const,
  calendar: () => [...contentKeys.all, 'calendar'] as const,
  stats: () => [...contentKeys.all, 'stats'] as const,
}

// Hooks

export function usePosts(filters: PostFilters = {}) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.postList(filters),
    queryFn: async (): Promise<readonly Post[]> => {
      const params = new URLSearchParams()
      if (filters.status && filters.status !== 'all') {
        params.set('status', filters.status)
      }
      if (filters.sort) {
        params.set('sort', filters.sort)
      }
      const query = params.toString() ? `?${params.toString()}` : ''
      const res = await apiFetch<readonly Post[]>(`/api/content/posts${query}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load posts')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function usePost(id: string) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.post(id),
    queryFn: async (): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`)
      if (!res.success || !res.data) throw new Error(res.error ?? `Post ${id} not found`)
      return res.data
    },
    enabled: Boolean(id),
  })
}

export function useContentCalendar() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.calendar(),
    queryFn: async (): Promise<CalendarEntry[]> => {
      const res = await apiFetch<CalendarEntry[]>('/api/content/calendar')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load content calendar')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useContentStats() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: contentKeys.stats(),
    queryFn: async (): Promise<ContentStats> => {
      const res = await apiFetch<ContentStats>('/api/content/stats')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load content stats')
      return res.data
    },
    staleTime: 60_000,
  })
}

export function useGeneratePost() {
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (request: GeneratePostRequest): Promise<Post> => {
      const res = await apiFetch<Post>('/api/content/generate', {
        method: 'POST',
        body: request,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to generate post')
      return res.data
    },
  })
}

export function useApprovePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`, {
        method: 'PUT',
        body: { status: 'approved' },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to approve post ${id}`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
    },
  })
}

export function useUpdatePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Post> }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`, {
        method: 'PUT',
        body: updates,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to update post ${id}`)
      return res.data
    },
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.post(id) })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}

export function useDeletePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const res = await apiFetch<unknown>(`/api/content/posts/${id}`, {
        method: 'DELETE',
      })
      if (!res.success) throw new Error(res.error ?? `Failed to delete post ${id}`)
      return id
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
      queryClient.removeQueries({ queryKey: contentKeys.post(id) })
    },
  })
}

export function useSchedulePost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, scheduledAt }: { id: string; scheduledAt: string }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}`, {
        method: 'PUT',
        body: { status: 'scheduled', scheduled_for: scheduledAt },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to schedule post ${id}`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}

/**
 * Refresh the post list, stats and engagement feed. Engagement itself is
 * collected by the hourly monitor; this button used to be labelled "Sync
 * Engagement" while only re-reading the engagement list, which promised a
 * LinkedIn sync that never happened.
 */
export function useSyncEngagement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentKeys.posts() }),
        queryClient.invalidateQueries({ queryKey: contentKeys.stats() }),
        queryClient.invalidateQueries({ queryKey: [...contentKeys.all, 'engagements'] }),
      ])
    },
  })
}

/** Set or clear a post's comment keyword trigger ("comment GUIDE below"). */
export function useSetDmTrigger() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, keyword, message }: { id: string; keyword: string | null; message?: string }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}/dm-trigger`, {
        method: 'PUT',
        body: { keyword, message },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to save the comment trigger')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
    },
  })
}

export function usePublishPost() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()

  return useMutation({
    mutationFn: async ({ id, accountId }: { id: string; accountId: string }): Promise<Post> => {
      const res = await apiFetch<Post>(`/api/content/posts/${id}/publish`, {
        method: 'POST',
        body: { account_id: accountId },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? `Failed to publish post ${id}`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}

// ---------------------------------------------------------------------------
// Auto-post campaigns (migration 0038) — recurring schedules created from a
// reviewed post in the generate dialog.
// ---------------------------------------------------------------------------

export type AutoSchedule = {
  readonly id: string
  readonly name: string
  readonly topic: string
  readonly content_type: string
  readonly tone: string | null
  readonly days_of_week: readonly number[]
  readonly hour_local: number
  readonly minute_local: number
  readonly timezone: string
  readonly auto_approve: boolean
  readonly active: boolean
  readonly last_run_date: string | null
  readonly last_post_id: string | null
  readonly next_run_at: string | null
  readonly created_at: string
}

export type AutoSchedulesResponse = {
  readonly schedules: readonly AutoSchedule[]
  readonly weekly_allowance: number
  /** Slots used by auto-post campaigns AND the Settings poster. */
  readonly weekly_slots_used: number
  /** Slots the Settings > Content LinkedIn Poster uses (0 when off). */
  readonly poster_slots?: number
  readonly max_schedules: number
}

export type CreateAutoScheduleInput = {
  readonly name: string
  readonly topic: string
  readonly content_type: string
  readonly tone?: string | null
  readonly days_of_week: readonly number[]
  readonly hour_local: number
  readonly minute_local: number
  readonly timezone: string
  readonly auto_approve: boolean
  /** The reviewed post that becomes the campaign's first publish. */
  readonly seed_post_id?: string
}

export const autoScheduleKeys = {
  all: ['content', 'auto-schedules'] as const,
}

export function useAutoSchedules(enabled = true) {
  const apiFetch = useApi()
  return useQuery({
    enabled,
    queryKey: autoScheduleKeys.all,
    queryFn: async (): Promise<AutoSchedulesResponse> => {
      const res = await apiFetch<AutoSchedulesResponse>('/api/content/auto-schedules')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load auto-post campaigns')
      return res.data
    },
    staleTime: 30_000,
  })
}

export function useCreateAutoSchedule() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateAutoScheduleInput) => {
      const res = await apiFetch<{ schedule: AutoSchedule | null; seeded_post_id: string | null; seeded_post_at: string | null }>(
        '/api/content/auto-schedules',
        { method: 'POST', body: input },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create the auto-post campaign')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: autoScheduleKeys.all })
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.calendar() })
    },
  })
}

export function useUpdateAutoSchedule() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateAutoScheduleInput> & { active?: boolean } }) => {
      const res = await apiFetch<{ schedule: AutoSchedule | null; unscheduled_posts?: number }>(`/api/content/auto-schedules/${id}`, {
        method: 'PUT',
        body: updates,
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to update the auto-post campaign')
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: autoScheduleKeys.all })
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
    },
  })
}

export function useDeleteAutoSchedule() {
  const apiFetch = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch<{ unscheduled_posts?: number }>(`/api/content/auto-schedules/${id}`, { method: 'DELETE' })
      if (!res.success) throw new Error(res.error ?? 'Failed to delete the auto-post campaign')
      return { id, unscheduled_posts: res.data?.unscheduled_posts ?? 0 }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: autoScheduleKeys.all })
      void queryClient.invalidateQueries({ queryKey: contentKeys.posts() })
      void queryClient.invalidateQueries({ queryKey: contentKeys.stats() })
    },
  })
}
